import type { Schema } from '@dealdesk/api-schema';

type Batch = Schema<'ImportBatch'>;

export const IMPORT_STEPS = ['columns', 'values', 'rows', 'commit'] as const;
export type ImportStep = (typeof IMPORT_STEPS)[number];

export type StepState = 'done' | 'current' | 'upcoming' | 'attention';

/** Customer lists have no value mappings: Columns → Rows → Commit (the result after commit). */
export function stepsFor(template: Batch['template']): readonly ImportStep[] {
  return template === 'customer_list' ? ['columns', 'rows', 'commit'] : IMPORT_STEPS;
}

/** Open items per step; a step with open items is shown as needing attention. */
export function openItems(b: Batch): Record<ImportStep, number> {
  return {
    columns: b.columns.filter((c) => c.required && !c.found).length,
    values: b.mappings.filter((m) => m.status === 'unmapped').length,
    rows: b.rows.filter((r) => r.resolution === 'conflict' || r.resolution === 'error').length,
    commit: 0,
  };
}

/** First step that still needs the user; the commit step when nothing is open. */
export function firstOpenStep(b: Batch): ImportStep {
  if (b.status !== 'preview') return 'commit';
  const open = openItems(b);
  return stepsFor(b.template).find((s) => open[s] > 0) ?? 'commit';
}

export function stepStates(b: Batch, current: ImportStep): Partial<Record<ImportStep, StepState>> {
  const open = openItems(b);
  const committed = b.status === 'committed';
  return Object.fromEntries(
    stepsFor(b.template).map((s) => {
      if (s === current) return [s, 'current'];
      if (committed) return [s, 'done'];
      if (open[s] > 0) return [s, 'attention'];
      return [s, s === 'commit' ? 'upcoming' : 'done'];
    }),
  );
}

/** Mapping values of one kind, unmapped first. */
export function mappingsByKind(b: Batch): [Schema<'ValueMappingKind'>, Schema<'ValueMapping'>[]][] {
  const order: Schema<'ValueMappingKind'>[] = ['user', 'team_leader', 'bank', 'stage', 'pending_with', 'employer', 'employer_category', 'nationality', 'product'];
  const rank = { unmapped: 0, suggested: 1, mapped: 2, ignored: 3 } as const;
  return order
    .map((k) => [k, b.mappings.filter((m) => m.kind === k).sort((x, y) => rank[x.status] - rank[y.status] || y.count - x.count)] as [Schema<'ValueMappingKind'>, Schema<'ValueMapping'>[]])
    .filter(([, list]) => list.length > 0);
}
